/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { memo, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FocusScope } from 'react-aria';
import { RemoveScroll } from 'react-remove-scroll';
import { useResizeDetector } from 'react-resize-detector';

import usePageBackground from './usePageBackground';
import { useSite } from '../../api/use';
import BackgroundImage from '../image/BackgroundImage';
import Image from '../image/Image';
import FigCaption from '../image/FigCaption';
import Container from './Container';
import AspectLimiter from './AspectLimiter';
import { PMILogoFull } from '../Logos';
import { Typography } from '../typography';
import MarketProportionsBackground from '../MarketProportionsBackground';
import TreaTechBackground, { Graphic } from '../TreaTechBackground';

export const PageBackgroundButton = ({ children, ...props }) => {
	const { setIsLightbox } = usePageBackground();

	return (
		<button
			onClick={() => setIsLightbox(true)}
			css={(theme) => [
				theme.resetButton,
				css`
					color: ${theme.color.bg};
					background-color: ${theme.color.fg};
					display: inline;
					padding: 0 0.5em 0 0.25em;
					border-radius: 2em;
					text-shadow: none;
				`,
			]}
			{...props}
		>
			<svg
				width="1em"
				height="1em"
				viewBox="0 0 16 16"
				css={css`
					overflow: visible;

					& * {
						vector-effect: non-scaling-stroke;
					}
				`}
			>
				<g fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth="1">
					<motion.path
						d="M2 7L5 10L2 13"
						initial={{ opacity: 0, x: 0 }}
						animate={{
							x: [0, 8],
							opacity: [0, 1, 0],
							transition: { repeat: Infinity, duration: 1 },
						}}
						exit={{ opacity: 0, x: 8, transition: { duration: 0.1 } }}
					/>
				</g>
			</svg>
			{children}
		</button>
	);
};

const Video = ({ html, aspect, fit = 'cover', thumbnail, ...props }) => {
	const { width, height, ref } = useResizeDetector({});

	const dim = useMemo(() => {
		const a = aspect * 0.01;
		const aB = height / width;

		if (fit === 'cover' ? a > aB : a < aB) {
			return { width: width + 'px', height: Math.round(width * a) + 'px' };
		} else {
			return { width: Math.round(height / a) + 'px', height: height + 'px' };
		}
	}, [fit, width, height, aspect]);

	return (
		<div ref={ref} {...props}>
			<div
				style={dim}
				dangerouslySetInnerHTML={{ __html: html }}
				css={css`
					position: absolute;
					left: 50%;
					top: 50%;
					transform: translate(-50%, -50%);

					& iframe {
						width: 100%;
						height: 100%;
					}
				`}
			/>
		</div>
	);
};

const NextIcon = () => {
	return (
		<motion.path
			d="M2 6L4 8L2 10" // M4 6L6 8L4 10 M6 6L8 8L6 10
			initial={{ opacity: 0, x: 0 }}
			animate={{
				x: [0, 8],
				opacity: [0, 1, 0],
				transition: { repeat: Infinity, duration: 1 },
			}}
			exit={{ opacity: 0, x: 8, transition: { duration: 0.1 } }}
		/>
	);
};

const LighboxButton = () => {
	const { image, video, graphics, isLightbox, setIsLightbox, hasReveal } = usePageBackground();

	const onClick = useCallback(() => {
		setIsLightbox((state) => !state);
	}, [setIsLightbox]);

	return createPortal(
		<div
			css={(theme) => css`
				position: fixed;
				z-index: ${theme.zIndex.pageBackgroundLightboxButton};
				left: 0;
				top: 50%;
				transform: translate(0, -50%);
			`}
		>
			<AnimatePresence>
				{(isLightbox || (!!hasReveal && !!(image || video || graphics))) && (
					<motion.div
						key={(image?.id || video?.thumbnail || graphics) + (isLightbox ? 'l' : 'n')}
						initial={{ opacity: 0, x: '-100%' }}
						animate={{ opacity: 1, x: '0%', transition: { delay: 0.9, ease: 'easeInOut', duration: 0.4 } }}
						exit={{ opacity: 0, x: '-100%', position: 'absolute' }}
						transition={{ ease: 'easeInOut', duration: 0.4 }}
					>
						<button
							css={(theme) => [
								theme.resetButton,
								css`
									overflow: hidden;
									background: ${theme.color.primaryDarkContrast};
									color: ${theme.color.primaryDark};
									border-top-right-radius: ${theme.dimension.container.paddingNarrow.s};
									border-bottom-right-radius: ${theme.dimension.container.paddingNarrow.s};

									display: block;
									width: calc(${theme.dimension.container.paddingNarrow.s} * 0.8);
									height: ${theme.dimension.container.paddingNarrow.s};

									@media (min-width: ${theme.breakpoint.m}) {
										width: calc(${theme.dimension.container.paddingNarrow.m} * 0.8);
										height: ${theme.dimension.container.paddingNarrow.m};
										border-top-right-radius: ${theme.dimension.container.paddingNarrow.m};
										border-bottom-right-radius: ${theme.dimension.container.paddingNarrow.m};
									}

									@media (min-width: ${theme.breakpoint.l}) {
										width: calc(${theme.dimension.container.paddingNarrow.l} * 0.8);
										height: ${theme.dimension.container.paddingNarrow.l};
										border-top-right-radius: ${theme.dimension.container.paddingNarrow.l};
										border-bottom-right-radius: ${theme.dimension.container.paddingNarrow.l};
									}
								`,
							]}
							onClick={onClick}
						>
							<svg
								fill="currentColor"
								version="1.1"
								viewBox="0 0 16 16"
								width="16"
								height="16"
								xmlns="http://www.w3.org/2000/svg"
								css={css`
									display: block;
									width: 80%;
									height: auto;
									overflow: visible;

									& * {
										vector-effect: non-scaling-stroke;
									}
								`}
							>
								<g fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth="1">
									<AnimatePresence>
										<motion.g
											key={isLightbox ? 'arrows' : 'cross'}
											initial={{ x: -16, opacity: 0, scale: 0 }}
											animate={{ x: 0, opacity: 1, scale: 1 }}
											exit={{ x: 16, opacity: 0, scale: 0 }}
										>
											{!isLightbox ? (
												<NextIcon />
											) : (
												<motion.path
													animate={{ scale: [1, 0.6, 1] }}
													transition={{ repeat: Infinity, duration: 1 }}
													d="M6 6L10 10M10 6L6 10"
												/>
											)}
										</motion.g>
									</AnimatePresence>
								</g>
							</svg>
						</button>
					</motion.div>
				)}
			</AnimatePresence>
		</div>,
		document.body
	);
};

const Lightbox = () => {
	const { image, video, isLightbox, caption, graphics } = usePageBackground();

	return (
		<AnimatePresence>
			{!!isLightbox && (
				<Container
					as={motion.div}
					initial={{ opacity: 1, x: '-100%' }}
					exit={{ opacity: 1, x: '100%' }}
					animate={{ opacity: 1, x: '0%' }}
					transition={{ ease: 'easeOut', duration: 1 }}
					limit={false}
					margin={false}
					css={(theme) => css`
						position: fixed;
						z-index: ${theme.zIndex.lightbox};
						left: 0;
						bottom: 0;
						width: 100%;
						height: 100%;
						background: ${theme.color.primaryDark};
						display: flex;
						flex-direction: column;
						overflow: hidden;
					`}
				>
					<RemoveScroll />
					<AspectLimiter
						aspect={image ? image.aspect : video ? video.aspect * 0.01 : 1}
						css={css`
							margin: auto;
							width: 100%;
							height: 80vh;
						`}
					>
						<motion.figure
							initial={{ opacity: 0, x: '100%' }}
							exit={{ opacity: 0, x: '-100%' }}
							animate={{ opacity: 1, x: '0%' }}
							transition={{ ease: 'easeOut', duration: 1 }}
							css={css`
								width: 100%;
								height: 100%;
								margin: 0;
							`}
						>
							{graphics ? (
								graphics.startsWith('TreaTech') ? (
									<Graphic currentStep={5} />
								) : null
							) : image ? (
								<Image {...image} noLightbox />
							) : video ? (
								<Video
									{...video}
									fit="contain"
									css={css`
										width: 100%;
										height: 100%;
									`}
								/>
							) : null}

							<motion.div
								initial={{ opacity: 0, x: '-10%' }}
								exit={{ opacity: 0, x: '10%' }}
								animate={{ opacity: 1, x: '0%' }}
								transition={{ ease: 'easeOut', duration: 1 }}
							>
								<FigCaption caption={caption} copyright={image?.copyright} />
							</motion.div>
						</motion.figure>
					</AspectLimiter>
				</Container>
			)}
		</AnimatePresence>
	);
};

const SponsoredBy = () => {
	const { sponsoredByText } = useSite();
	return (
		<motion.div
			initial={{ y: '100%' }}
			animate={{ y: '0%' }}
			exit={{ y: '100%' }}
			transition={{ ease: 'easeInOut', duration: 1 }}
			css={css`
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
			`}
		>
			<Container
				narrow
				margin={false}
				css={css`
					text-align: center;
					margin-bottom: 1em;
				`}
			>
				<Typography
					size="xs"
					css={css`
						margin-bottom: 1em;
						white-space: pre-line;
					`}
				>
					{sponsoredByText}
				</Typography>
				<PMILogoFull
					css={css`
						margin: 0 auto;
						max-width: 14em;
					`}
				/>
			</Container>
		</motion.div>
	);
};

const PageBackground = () => {
	const { tinted, masked, maskOpacity, image, video, isLightbox, hasSponsoredby, graphics } = usePageBackground();
	const graphicsKey = useMemo(() => {
		if (!graphics) return null;
		if (graphics.startsWith('MarketProportions')) {
			return 'MarketProportions';
		}
		if (graphics.startsWith('TreaTech')) {
			return 'TreaTech';
		}
		return graphics;
	}, [graphics]);

	return (
		<>
			<div
				css={(theme) => css`
					position: fixed;
					z-index: ${theme.zIndex.pageBackground};
					left: 0;
					top: 0;
					bottom: 0;
					right: 0;

					isolation: isolate;
					background: ${theme.color.primaryDark};
				`}
			>
				<AnimatePresence>
					{!!(graphicsKey || image || video) && (
						<motion.div
							key={graphicsKey || image?.id || video.thumbnailUrl}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							css={css`
								position: absolute;
								inset: 0;
								display: flex;
								align-items: center;
								justify-content: center;
							`}
						>
							{graphicsKey ? (
								<div
									css={(theme) =>
										css`
											flex: 1 1 auto;
											max-width: ${theme.dimension.maxWidth};
											padding: 2em;
										`
									}
								>
									{graphicsKey === 'MarketProportions' ? (
										<MarketProportionsBackground revealed={graphics === 'MarketProportionsRevealed'} />
									) : (
										<TreaTechBackground revealed={graphics === 'TreaTechRevealed'} />
									)}
								</div>
							) : image ? (
								<BackgroundImage
									image={image}
									showCopyright={false}
									css={css`
										position: absolute;
										inset: 0;
									`}
								/>
							) : (
								<Video
									{...video}
									css={css`
										position: absolute;
										inset: 0;
									`}
								/>
							)}
						</motion.div>
					)}
					{!!tinted && (
						<motion.div
							key="tint"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							css={(theme) => css`
								position: absolute;
								inset: 0;
								background: ${theme.color.primaryDark};
								mix-blend-mode: overlay;
							`}
						/>
					)}
					{!!masked && (
						<motion.div
							key="mask"
							initial={{ opacity: 0 }}
							animate={{ opacity: maskOpacity }}
							exit={{ opacity: 0 }}
							css={css`
								position: absolute;
								inset: 0;
								background: radial-gradient(ellipse at top left, rgba(13, 93, 169, 0) 0%, rgba(5, 41, 76, 1) 100%),
									radial-gradient(ellipse at bottom right, rgba(13, 93, 169, 0) 0%, rgba(5, 41, 76, 1) 100%);
							`}
						/>
					)}
					{!!hasSponsoredby && <SponsoredBy key="sponsored" />}
				</AnimatePresence>
			</div>
			<FocusScope contain={isLightbox}>
				<Lightbox />
				<LighboxButton />
			</FocusScope>
		</>
	);
};

export default memo(PageBackground);
